import React, { useState, useEffect } from 'react';
import Stepper from './Stepper';
import { Button } from '../ui';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * StepperWrapper
 * - Renders the Stepper header
 * - Renders the child corresponding to the active step
 * - Provides Prev / Next / Finish buttons
 *
 * Props:
 * - steps: [{ title, description? }]
 * - initialStep (number)
 * - onFinish () => void
 * - onStepChange (index) => void
 * - children: React nodes (one child per step) or a render function
 * canProceed can be either:
 * - a function: (activeIndex) => boolean
 * - a boolean: whether Next is enabled globally
 * default: always true
 * AdditionalComponent: A React component to render below the buttons and above the step content
 */
const StepperWrapper = ({
  steps = [],
  initialStep = 0,
  onFinish,
  onStepChange,
  children,
  className = '',
  hideButtons = (stepIndex) => false,
  canProceed = () => true,
  AdditionalComponent = null,
  stepsWrapperClassName = '',
  hideStepper = false,
  disableBackButton = (stepIndex) => false,
  hideBackButton = (stepIndex) => false,
}) => {
  const [active, setActive] = useState(initialStep);
  const { isMobile } = useMediaQuery();
  // Update active step when initialStep prop changes
  useEffect(() => {
    setActive(initialStep);
  }, [initialStep]);

  const childArray = React.Children.toArray(children);

  const goTo = (index) => {
    const i = Math.max(0, Math.min(index, steps.length - 1));
    setActive(i);
    if (onStepChange) onStepChange(i);
  };

  const handlePrev = () => goTo(active - 1);
  const isNextAllowed = () => {
    if (typeof canProceed === 'function') return canProceed(active);
    return !!canProceed;
  };

  const handleNext = () => {
    if (!isNextAllowed()) return;

    if (active >= steps.length - 1) {
      if (onFinish) onFinish();
      return;
    }
    goTo(active + 1);
  };

  return (
    <div className={`w-full flex flex-col ${className}`}>
      {!hideStepper && <Stepper steps={steps} active={active} onStepClick={goTo} />}
      {AdditionalComponent ? <AdditionalComponent /> : null}

      {/* Step content — grows to fill available space */}
      <div className={`m-0 md:mt-4 flex flex-col flex-1 min-h-0 ${stepsWrapperClassName}`}>
        {childArray[active] ?? null}
      </div>

      {!hideButtons(active) && (
        // Back is visible when the prop allows it AND we're not on step 0.
        // Use this to switch between justify-between (two buttons) and
        // justify-end (Next/Finish only).
        (() => {
          const showBack = !hideBackButton(active) && active > 0;
          return (
            <div
              className={`sticky bottom-0 z-10 flex items-center gap-3 py-3 px-1 backdrop-blur-sm mt-2 ${
                showBack ? 'justify-between' : 'justify-end'
              }`}
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              {showBack && (
                <Button
                  variant='primary'
                  size={isMobile ? 'sm' : 'md'}
                  onClick={handlePrev}
                  disabled={disableBackButton(active)}
                >
                  Back
                </Button>
              )}
              <Button
                variant='primary'
                size={isMobile ? 'sm' : 'md'}
                onClick={handleNext}
                disabled={!isNextAllowed()}
              >
                {active >= steps.length - 1 ? 'Finish' : 'Next'}
              </Button>
            </div>
          );
        })()
      )}
    </div>
  );
};

export default StepperWrapper;
